import React, { useContext, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import mainStyles from "styles";

import { PlanType } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/admin/v1/subscriptions_pb";
import { ComponentType } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/components_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import { ChargifyState } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_payment_pb";
import { SubscriptionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import CancelSubscription from "./subscription-actions/cancel-subscription";
import ChangeBillingFrequency from "./subscription-actions/change-billing-frequency";
import UpdateCancelAtEndOfPeriod from "./subscription-actions/change-cancel-at-end-of-period";
import ChangeComponentPricePoint from "./subscription-actions/change-component-price-point";
import ChangeContractTerm from "./subscription-actions/change-contract-term";
import ChangeProductType from "./subscription-actions/change-product-type";
import ChangeSubscriptionCurrency from "./subscription-actions/change-subscription-currency";
import ChangeTrialDate from "./subscription-actions/change-trial-date";
import ConvertTrialButton from "./subscription-actions/convert-trial-method";
import DowngradeToFreePlan from "./subscription-actions/downgrade-to-free-plan";
import ManageCoupons from "./subscription-actions/manage-coupons";
import PauseSync from "./subscription-actions/pause-sync";
import ReactivateSubscription from "./subscription-actions/reactivate-subscription";
import ReconcileSubscription from "./subscription-actions/reconcile-subscription";
import RemovePaymentMethod from "./subscription-actions/remove-payment-method";
import SetCustomField from "./subscription-actions/set-custom-field";
import SyncPaymentMethod from "./subscription-actions/sync-payment-method";
import UpdatePaymentCollectionMethod from "./subscription-actions/update-payment-collection-method";
import UpdateSeatMovementsRestriction from "./subscription-actions/update-seat-movements-restriction";
import UpdateTaxExemption from "./subscription-actions/update-tax-exemption";
import UpgradeWithoutCreditCard from "./subscription-actions/upgrade-without-credit-card";
import DisplayHeading from "components/shared/display-heading";
import { OrgDataContext, SubDataContext } from "context/organisation-context";
import { PermissionsContext } from "context/permissions-context";
import { isUnderZuoraMigration } from "helpers/subscriptions";

interface SubscriptionsTabProps {
  refreshOrgData: () => void;
}

const SubscriptionsTab: React.FC<SubscriptionsTabProps> = ({
  refreshOrgData,
}: SubscriptionsTabProps) => {
  const classes = mainStyles();
  const theme = useTheme();
  const { orgData } = useContext(OrgDataContext);
  const { subData } = useContext(SubDataContext);
  const { hasPermissions, hasGroups, permissions } =
    useContext(PermissionsContext);
  const subscription = useMemo(() => {
    return orgData.subscription && orgData.subscription[0];
  }, [orgData]);

  const zuoraSubscription = subData?.subscription;

  const isMigrating = isUnderZuoraMigration(
    zuoraSubscription?.zuoraMigrationStatus
  );

  const [migrationAcknowledged, setMigrationAcknowledged] = useState(false);

  const [subPlanType, setSubPlanType] = useState(PlanType.UNSPECIFIED);

  const refreshSubPlanType = (newPlanType: PlanType) => {
    setSubPlanType(newPlanType);
  };

  const canUpdateContractTerm =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.UPDATE_CONTRACT_TERM]);

  const canPauseSalesForceFync =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.PAUSE_SALESFORCE_SYNC]);

  const canReconcileSubscription =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.RECONCILE_SUBSCRIPTION]);

  const canUpdateBillingFrequency =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.UPDATE_BILLING_FREQUENCY]);

  const canSyncPaymentMethod =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.SYNC_PAYMENT_METHOD]);

  const isFreeOrg =
    subscription?.type === SubscriptionType.FREE ||
    subscription?.type === SubscriptionType.FREE_ORG;

  const isOnChargify =
    (subscription?.chargify?.id && subscription?.chargify?.id?.length > 0) ||
    false;

  const shouldDisplayChangeBillingFreq =
    !isFreeOrg &&
    canUpdateBillingFrequency &&
    subscription?.chargify?.state !== undefined &&
    [ChargifyState.TRIALING, ChargifyState.ACTIVE].includes(
      subscription?.chargify?.state
    );

  const shouldDisplayChangeContractTerm =
    !isFreeOrg &&
    canUpdateContractTerm &&
    subscription?.chargify?.state != undefined &&
    [ChargifyState.TRIALING, ChargifyState.ACTIVE].includes(
      subscription?.chargify?.state
    );

  const trialCancelEndOfPeriodRestrictedGroups =
    !permissions.isPermissionsLoading &&
    hasGroups(["App - Business Tools - AE", "App - Business Tools - BD"]);

  const canCancelAtEndOfPeriodState =
    subscription?.chargify?.state &&
    ![ChargifyState.CANCELED, ChargifyState.PAST_DUE].includes(
      subscription?.chargify?.state
    );

  const renderCancelAtEndOfPeriod = () => {
    if (
      (!isFreeOrg &&
        canCancelAtEndOfPeriodState &&
        trialCancelEndOfPeriodRestrictedGroups &&
        subscription?.chargify?.state === ChargifyState.TRIALING &&
        isOnChargify) ||
      (!isFreeOrg &&
        canCancelAtEndOfPeriodState &&
        !trialCancelEndOfPeriodRestrictedGroups &&
        isOnChargify)
    ) {
      return (
        <TableRow>
          <TableCell sx={classes.tableCellNew}>
            <UpdateCancelAtEndOfPeriod refreshOrgData={refreshOrgData} />
          </TableCell>
        </TableRow>
      );
    }
  };

  if (isMigrating && !migrationAcknowledged) {
    return (
      <Grid container>
        <Grid item xs={12} sx={classes.paddingTopSmall}>
          <DisplayHeading>Manage Subscription</DisplayHeading>
        </Grid>
        <Grid item xs={12}>
          <Alert severity="warning" sx={{ textAlign: "start", m: 2 }}>
            <Typography
              variant="body1"
              gutterBottom
              sx={{ fontWeight: "bold" }}
            >
              Zuora Billing Migration In Progress
            </Typography>
            <Typography variant="body2" gutterBottom>
              This organisation is currently undergoing a Zuora billing
              migration. Billing changes may be rejected or cause unexpected
              issues during this period.
            </Typography>
            <Typography variant="body2" gutterBottom>
              Please avoid making billing changes unless absolutely necessary.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
              onClick={() => setMigrationAcknowledged(true)}
            >
              I Understand, Proceed
            </Button>
          </Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container>
      <Grid item xs={12} sx={classes.paddingTopSmall}>
        <DisplayHeading>Manage Subscription</DisplayHeading>
      </Grid>
      <Grid item xs={12}>
        <Box
          style={{
            borderColor: theme.palette.divider,
            borderWidth: "1px",
            borderStyle: "solid",
            borderRadius: "5px",
            margin: "20px",
          }}
        >
          <Table size="medium">
            <TableBody>
              {!isFreeOrg &&
                !isOnChargify &&
                subscription.chargify?.state === ChargifyState.TRIALING && (
                  <TableRow>
                    <TableCell sx={classes.tableCellNew}>
                      <Grid>
                        <ConvertTrialButton refreshOrgData={refreshOrgData} />
                      </Grid>
                    </TableCell>
                  </TableRow>
                )}

              {isOnChargify && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <Grid>
                      <UpdatePaymentCollectionMethod />
                    </Grid>
                  </TableCell>
                </TableRow>
              )}

              <TableRow>
                <TableCell sx={classes.tableCellNew}>
                  <RemovePaymentMethod refreshOrgData={refreshOrgData} />
                </TableCell>
              </TableRow>

              {!isFreeOrg && canSyncPaymentMethod && isOnChargify && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <SyncPaymentMethod refreshOrgData={refreshOrgData} />
                  </TableCell>
                </TableRow>
              )}

              {canPauseSalesForceFync && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <PauseSync />
                  </TableCell>
                </TableRow>
              )}

              {canReconcileSubscription && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <ReconcileSubscription refreshOrgData={refreshOrgData} />
                  </TableCell>
                </TableRow>
              )}

              {isOnChargify && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <UpdateTaxExemption />
                  </TableCell>
                </TableRow>
              )}

              {!isFreeOrg && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <ChangeProductType />
                  </TableCell>
                </TableRow>
              )}

              {shouldDisplayChangeContractTerm && isOnChargify && (
                <>
                  <TableRow>
                    <TableCell sx={classes.tableCellNew}>
                      <ChangeContractTerm refreshOrgData={refreshOrgData} />
                    </TableCell>
                  </TableRow>
                </>
              )}

              {shouldDisplayChangeBillingFreq && isOnChargify && (
                <>
                  <TableRow>
                    <TableCell sx={classes.tableCellNew}>
                      <ChangeBillingFrequency />
                    </TableCell>
                  </TableRow>
                </>
              )}

              {/* Only display cancel at end of period if subscription is not past_due or canceled*/}
              {renderCancelAtEndOfPeriod()}

              <TableRow>
                <TableCell sx={classes.tableCellNew}>
                  <CancelSubscription refreshOrgData={refreshOrgData} />
                </TableCell>
              </TableRow>

              {/* ONLY RENDER TRIAL DATE COMPONENT WHEN SUBSCRIPTION IS TRIALING */}
              {subscription?.chargify?.state === ChargifyState.TRIALING && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <ChangeTrialDate subscription={subscription} />
                  </TableCell>
                </TableRow>
              )}

              {/* Only display upgrade if subscription is a free organisation*/}
              {isFreeOrg && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <UpgradeWithoutCreditCard refreshOrgData={refreshOrgData} />
                  </TableCell>
                </TableRow>
              )}

              {/* Only display reactivate option if subscription is canceled*/}
              {subscription?.chargify?.state === ChargifyState.CANCELED && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <ReactivateSubscription refreshOrgData={refreshOrgData} />
                  </TableCell>
                </TableRow>
              )}

              {/* Only display downgrade option if subscriptions is not a free organisation */}
              {!isFreeOrg && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <DowngradeToFreePlan refreshOrgData={refreshOrgData} />
                  </TableCell>
                </TableRow>
              )}

              {isOnChargify && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <UpdateSeatMovementsRestriction
                      subscription={subscription}
                    />
                  </TableCell>
                </TableRow>
              )}

              {!isFreeOrg && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <ManageCoupons />
                  </TableCell>
                </TableRow>
              )}

              <TableRow>
                <TableCell sx={classes.tableCellNew}>
                  <ChangeSubscriptionCurrency refreshOrgData={refreshOrgData} />
                </TableCell>
              </TableRow>

              {/* Only show Subscription type and price if they have plans */}
              {!isFreeOrg && isOnChargify && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <ChangeComponentPricePoint
                      subscription={subscription}
                      componentType={ComponentType.FULL_SEAT}
                      onPlanTypeUpdate={refreshSubPlanType}
                    />
                  </TableCell>
                </TableRow>
              )}

              {!isFreeOrg && isOnChargify && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <ChangeComponentPricePoint
                      subscription={subscription}
                      componentType={ComponentType.LITE_SEAT}
                      onPlanTypeUpdate={refreshSubPlanType}
                    />
                  </TableCell>
                </TableRow>
              )}

              {subscription.components && isOnChargify && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <SetCustomField
                      subscription={subscription}
                      planType={subPlanType}
                    />{" "}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Grid>
    </Grid>
  );
};

export default SubscriptionsTab;
